import React from 'react';
import { connect } from 'react-redux';
import AccessPermissionChecker from '../common/AccessPermissionChecker';
import { PERMISSION_NAMES } from '../../utils/AuthUtils';
import { withUserType } from '../withUserType';
import { Card, Col, Collapse, Form, Input, Row, Switch, Table } from 'antd';
import {
	getEntitiesByIdWithSearch,
	nullSafeGet,
	nullSafeGetOrElse,
} from '../../utils/DataAccessUtils';
import {
	fetchSuppliers,
	updateFiltersSuppliers,
	updatePaginationSuppliers,
} from '../../thunks/wrenchhub_thunks';
import { defaultPagination } from '../WorkOrderTable/config/pagination/pagination';
import MapboxGallery from '../mapboxgallery/MapboxGallery';
import WrenchHubCategoryFilters from './WrenchHubCategoryFilters';
import SupplierRecord from './SupplierRecord';
import LocationAutocompleteWrapper from './LocationAutocompleteWrapper';
import { ROLE_TYPES } from '../../utils/DataConstants';
import {
	supplierPrivateNetworksGetDistinctForBuyer,
	supplierPrivateNetworksGetDistinctForSupplier,
} from '../../thunks/supplier_private_networks_thunks';

const TARGETCOLLECTIONNAME = 'wrench_hub';
const DEFAULT_RADIUS = '50km';

function WrenchHub({
	userType,
	location,
	data,
	fetchData,
	suppliers,
	updateFilters,
	updatePagination,
	fetchDistinctSupplierPrivateNetwork,
}) {
	const [privateNetwork, setPrivateNetwork] = React.useState([]);
	const fetchPrivateNetwork = React.useCallback(() => {
		fetchDistinctSupplierPrivateNetwork({}).then((spns) => {
			setPrivateNetwork(spns);
		});
	}, [fetchDistinctSupplierPrivateNetwork]);
	React.useEffect(() => {
		const queryParams = new URLSearchParams(location.search);
		const preserve = queryParams.has('preserve');

		if (!preserve || !(data && data.length)) {
			fetchData(location);
		}
		fetchPrivateNetwork();
	}, []);

	const columns = [
		{
			title: 'Name',
			dataIndex: 'displayName',
			render: (text, record) => (
				<SupplierRecord
					onAddNetwork={fetchPrivateNetwork}
					record={record}
					privateNetwork={privateNetwork}
				/>
			),
		},
	];
	return (
		<AccessPermissionChecker name={PERMISSION_NAMES.MODIFY_SUPPLIERS} isPage>
			<Row gutter={16} style={{ marginBottom: 16 }}>
				<Col span={16}>
					<Row gutter={8} style={{ marginBottom: 16 }}>
						<Col span={12}>
							<Input
								size="large"
								placeholder="Search for a service provider..."
								onChange={({ target: { value } }) => updateFilters({ search: value.trim() })}
							/>
						</Col>
						<Col span={12}>
							<LocationAutocompleteWrapper
								onChange={({ lat, lng }) =>
									updateFilters({ lat, lon: lng, radius: DEFAULT_RADIUS })
								}
								onClear={() => updateFilters({ lat: undefined, lon: undefined, radius: undefined })}
							/>
						</Col>
					</Row>
					<Table
						loading={suppliers.fetching}
						rowClassName={'clickable-index-row'}
						style={{ tableLayout: 'fixed' }}
						className="table-fixed-100"
						showHeader={false}
						pagination={{
							...defaultPagination,
							...suppliers.pagination,
							onChange: (current, pageSize) => updatePagination({ current, pageSize }),
						}}
						dataSource={data.map((c) => ({
							...(c || {}),
							categoryNames: Array.isArray(nullSafeGetOrElse('categoryNames', c, []))
								? nullSafeGetOrElse('categoryNames', c, []).join(', ')
								: nullSafeGet('categoryNames', c),
						}))}
						columns={columns}
						rowKey={(record) => parseInt(record.id)}
					/>
				</Col>
				<Col span={8}>
					<Card style={{ height: 400 }}>
						<MapboxGallery
							showNavControls={true}
							popupRenderFunc={(gle) => {
								let addressDisplayParts = [];
								if (gle.primaryAddress.streetAddress1) {
									addressDisplayParts.push(`<div>${gle.primaryAddress.streetAddress1}</div>`);
								}
								if (gle.primaryAddress.streetAddress2) {
									addressDisplayParts.push(`<div>${gle.primaryAddress.streetAddress2}</div>`);
								}
								addressDisplayParts.push(`<div>`);
								if (gle.primaryAddress.city) {
									addressDisplayParts.push(`<span>${gle.primaryAddress.city}</span>`);
								}
								if (gle.primaryAddress.region) {
									if (gle.primaryAddress.city) {
										addressDisplayParts.push(`<span>, ${gle.primaryAddress.region}</span>`);
									} else {
										addressDisplayParts.push(`<span>${gle.primaryAddress.region}</span>`);
									}
								}
								if (gle.primaryAddress.postcode) {
									addressDisplayParts.push(` <span>${gle.primaryAddress.postcode}</span>`);
								}
								addressDisplayParts.push(`</div>`);
								if (gle.primaryAddress.country) {
									addressDisplayParts.push(`<div>${gle.primaryAddress.country}</div>`);
								}
								const addressDisplay = addressDisplayParts.join('');
								const logoImage = gle.logoUrl
									? `<div style="margin-right: 16px; width: 64px; height: 64px; background: url(${gle.logoUrl}) center center / contain no-repeat white;"></div>`
									: `<div style="margin-right: 16px; width: 64px; height: 64px; background: url(https://s3.amazonaws.com/emptor-data-assets/images/empty-state/icons8-city-filled-100.png) center center / contain no-repeat white;"></div>`;
								return `
																									<div style="padding: 16px; display: flex; align-items: center;">
																											${logoImage}
																											<div>
																													<div style="font-weight: bold">
																															<a href="/${userType}/suppliers/detail/${gle.id}">${gle.displayName}</a>
																													</div>
																													<div style="color: rgba(0,0,0,0.65)">
																															${addressDisplay}
																													</div>
																											</div>
																									</div>
																									`;
							}}
							geolocatedEntities={data.filter((item) => item.latitude && item.longitude)}
							baseLayerStyle="light"
							handleSearchAgain={(bounds, center) => {
								if (center && center.length) {
									const [lon, lat] = center;
									updateFilters({ lat, lon, radius: DEFAULT_RADIUS });
								}
							}}
						/>
					</Card>
					<WrenchHubCategoryFilters suppliersStateSlice={suppliers} updateFilters={updateFilters} />
					<Row style={{ margin: '0.5em -8px' }} gutter={16}>
						<Col span={24}>
							<Collapse defaultActiveKey={['2']}>
								<Collapse.Panel header="Diversity Status" key="2">
									<div style={{ padding: '0 8px' }}>
										<Form>
											{[
												{ label: 'Minority owned', key: 'isMinorityOwned' },
												{ label: 'Women owned', key: 'isWomenOwned' },
												{ label: 'Veteran owned', key: 'isVeteranOwned' },
												{ label: 'Small business', key: 'isSmallBusinessEnterprise' },
											].map(({ label, key }) => (
												<Form.Item key={key} label={label}>
													<Switch
														onChange={(checked) =>
															updateFilters({ [key]: checked ? true : undefined })
														}
														checked={suppliers.filters[key] === true}
													/>
												</Form.Item>
											))}
										</Form>
									</div>
								</Collapse.Panel>
							</Collapse>
						</Col>
					</Row>
				</Col>
			</Row>
		</AccessPermissionChecker>
	);
}

const mapStateToProps = (state: any, ownProps) => ({
	data: getEntitiesByIdWithSearch(
		state.supplier_facilities[ownProps.targetCollectionName || TARGETCOLLECTIONNAME]
			? state.supplier_facilities[ownProps.targetCollectionName || TARGETCOLLECTIONNAME]
					.recordsIds || []
			: [],
		state.supplier_facilities
	),
	suppliers: state.supplier_facilities[ownProps.targetCollectionName || TARGETCOLLECTIONNAME],
});

const mapDispatchToProps = (dispatch: any, ownProps) => ({
	fetchData: (location) =>
		dispatch(
			fetchSuppliers(ownProps.userType)(
				ownProps.handleQueryParams,
				ownProps.history,
				location,
				ownProps.targetCollectionName || TARGETCOLLECTIONNAME,
				ownProps.preAppliedFilters,
				ownProps.initialPagination,
				ownProps.initialSorting || { sort_by: 'completedWorkOrderCount', order: 'desc' },
				ownProps.initialFilters,
				ownProps.ignoreRefreshCounts
			)
		),
	updateFilters: (filters) =>
		dispatch(
			updateFiltersSuppliers(ownProps.userType)(
				ownProps.handleQueryParams,
				ownProps.history,
				ownProps.targetCollectionName || TARGETCOLLECTIONNAME,
				filters,
				ownProps.ignoreRefreshCounts
			)
		),
	updatePagination: (pagination) =>
		dispatch(
			updatePaginationSuppliers(ownProps.userType)(
				ownProps.handleQueryParams,
				ownProps.history,
				ownProps.targetCollectionName || TARGETCOLLECTIONNAME,
				pagination,
				ownProps.ignoreRefreshCounts
			)
		),
	fetchDistinctSupplierPrivateNetwork: (params) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? supplierPrivateNetworksGetDistinctForSupplier(params, 'supplierPrivateNetworksIndex', {})
				: supplierPrivateNetworksGetDistinctForBuyer(params, 'supplierPrivateNetworksIndex', {})
		),
});

export default withUserType(connect(mapStateToProps, mapDispatchToProps)(WrenchHub));
